import {
  Avatar,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  MenuItem,
  Slider,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import { useMemo, useState } from "react";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import SaveRoundedIcon from "@mui/icons-material/SaveRounded";
import UploadTwoToneIcon from "@mui/icons-material/UploadTwoTone";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { doc, setDoc } from "firebase/firestore";
import { storage, db } from "../../firebase/firebase";

const VOICES = [
  "alloy",
  "ash",
  "ballad",
  "coral",
  "echo",
  "fable",
  "nova",
  "onyx",
  "sage",
  "shimmer",
];

const formatSpacing = (h) => {
  if (h < 1) return `${Math.round(h * 60)}m`;
  if (Number.isInteger(h / 24)) {
    const d = h / 24;
    return `${d} day${d > 1 ? "s" : ""}`;
  }
  return `${h}h`;
};

export default function EditInstructionDialog({ open, coach, onClose, uid }) {
  const theme = useTheme();
  const accentTone = useMemo(
    () => theme.palette.secondary?.main ?? theme.palette.primary.light ?? theme.palette.primary.main,
    [theme.palette.primary.light, theme.palette.primary.main, theme.palette.secondary?.main]
  );
  const [name, setName] = useState(coach.name);
  const [instr, setInstr] = useState(coach.instructions);
  const [systemPrompt, setSystemPrompt] = useState(coach.systemPrompt || "");
  const [userPrompt, setUserPrompt] = useState(coach.userPrompt || "");
  const [spacing, setSpacing] = useState(
    coach.spacing ? coach.spacing / 60 : 24
  );
  const [voice, setVoice] = useState(coach.voice || "alloy");
  const [avatarFile, setAvatarFile] = useState(null);
  const [saving, setSaving] = useState(false);

  /** uploads avatar if chosen, returns URL (or null) */
  const uploadAvatar = async () => {
    if (!avatarFile) return null;
    const fileRef = ref(storage, `avatars/${uid}/${coach.id}`);
    await uploadBytes(fileRef, avatarFile);
    return getDownloadURL(fileRef);
  };

  const handleSave = async () => {
    setSaving(true);
    const photoURL = await uploadAvatar();
    const dataToSave = {
      name,
      spacing: Number(spacing) * 60,
      voice,
      ...(photoURL ? { photoURL } : {}),
    };

    if (coach.systemPrompt !== undefined || coach.userPrompt !== undefined) {
      dataToSave.systemPrompt = systemPrompt;
      dataToSave.userPrompt = userPrompt;
    } else {
      dataToSave.instructions = instr;
    }

    await setDoc(doc(db, "users", uid, "agents", coach.id), dataToSave, {
      merge: true,
    });
    setSaving(false);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 4,
          overflow: "hidden",
          background: (theme) =>
            theme.palette.mode === "light"
              ? `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.18)} 0%, ${alpha(accentTone, 0.12)} 30%, ${alpha(theme.palette.background.paper, 0.95)} 100%)`
              : `linear-gradient(135deg, ${alpha(theme.palette.primary.dark ?? theme.palette.primary.main, 0.55)} 0%, ${alpha(accentTone, 0.35)} 28%, ${alpha(theme.palette.background.paper, 0.82)} 100%)`,
          border: (theme) =>
            `1px solid ${alpha(theme.palette.primary.main, theme.palette.mode === "light" ? 0.24 : 0.45)}`,
          boxShadow: (theme) => theme.shadows[12],
          backdropFilter: "blur(26px)",
        },
      }}
    >
      <DialogTitle
        data-testid="edit-agent-dialog-title"
        sx={{
          px: { xs: 3, md: 4 },
          pt: { xs: 3, md: 3.5 },
          pb: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
        }}
      >
        <Box>
          <Typography variant="overline" sx={{ letterSpacing: 1.5, opacity: 0.65 }}>
            Coach Persona
          </Typography>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            Edit coach
          </Typography>
        </Box>
        <Button
          onClick={onClose}
          startIcon={<CloseRoundedIcon />}
          sx={{
            borderRadius: 999,
            px: 2,
            py: 0.75,
            color: alpha(theme.palette.text.primary, 0.7),
            '&:hover': {
              backgroundColor: alpha(theme.palette.text.primary, 0.08),
            },
          }}
        >
          Close
        </Button>
      </DialogTitle>
      <DialogContent sx={{ px: { xs: 3, md: 4 }, pb: { xs: 2, md: 3 }, pt: 0 }}>
        <Stack spacing={3} sx={{ mt: 1.5 }}>
          <TextField
            label="Name"
            fullWidth
            value={name}
            onChange={(e) => setName(e.target.value)}
            data-testid="edit-agent-name-input"
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
              },
            }}
          />
          {coach.systemPrompt !== undefined || coach.userPrompt !== undefined ? (
            <>
              <TextField
                label="System Prompt"
                multiline
                minRows={6}
                fullWidth
                value={systemPrompt}
                onChange={(e) => setSystemPrompt(e.target.value)}
                data-testid="edit-agent-system-prompt-textarea"
              />
              <TextField
                label="User Prompt"
                multiline
                minRows={6}
                fullWidth
                value={userPrompt}
                onChange={(e) => setUserPrompt(e.target.value)}
                data-testid="edit-agent-user-prompt-textarea"
              />
            </>
          ) : (
            <TextField
              label="Instructions"
              multiline
              minRows={6}
              fullWidth
              value={instr}
              onChange={(e) => setInstr(e.target.value)}
              data-testid="edit-agent-instructions-textarea"
            />
          )}
          <Stack direction={{ xs: "column", md: "row" }} spacing={3}>
            <TextField
              select
              label="Voice"
              value={voice}
              onChange={(e) => setVoice(e.target.value)}
              data-testid="edit-agent-voice-select"
              sx={{
                width: { xs: "100%", md: 220 },
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  backgroundColor: (theme) =>
                    theme.palette.mode === "light"
                      ? alpha(theme.palette.common.white, 0.95)
                      : alpha(theme.palette.background.default, 0.55),
                },
              }}
            >
              {VOICES.map((v) => (
                <MenuItem key={v} value={v}>
                  {v}
                </MenuItem>
              ))}
            </TextField>
            <Stack spacing={1} sx={{ flex: 1 }}>
              <Typography variant="caption" sx={{ letterSpacing: 1, textTransform: "uppercase", opacity: 0.65 }}>
                Nudge spacing
              </Typography>
              <Stack direction="row" spacing={2} alignItems="center">
                <Slider
                  min={0.25}
                  max={24}
                  step={0.25}
                  value={spacing}
                  onChange={(_, v) => setSpacing(v)}
                  marks={[
                    { value: 0.25, label: "15m" },
                    { value: 1, label: "1h" },
                    { value: 6, label: "6h" },
                    { value: 12, label: "12h" },
                    { value: 24, label: "1 day" },
                  ]}
                  sx={{ flex: 1 }}
                  data-testid="edit-agent-spacing-slider"
                />
                <Typography
                  variant="body2"
                  data-testid="edit-agent-spacing-value"
                  sx={{ minWidth: 48, textAlign: "right", fontWeight: 600 }}
                >
                  {formatSpacing(spacing)}
                </Typography>
              </Stack>
            </Stack>
          </Stack>
          <Divider sx={{ borderColor: alpha(theme.palette.primary.main, 0.12) }} />
          <Stack direction={{ xs: "column", md: "row" }} spacing={3} alignItems={{ md: "center" }}>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Avatar
                src={avatarFile ? URL.createObjectURL(avatarFile) : coach.photoURL}
                data-testid="edit-agent-avatar-preview"
                sx={{
                  width: 72,
                  height: 72,
                  border: (theme) => `2px solid ${alpha(theme.palette.primary.main, 0.35)}`,
                  boxShadow: (theme) => theme.shadows[4],
                }}
              />
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  Coach avatar
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.7 }}>
                  Upload a square image (PNG or JPG). We handle the rest.
                </Typography>
              </Box>
            </Stack>
            <Button
              variant="outlined"
              component="label"
              data-testid="edit-agent-avatar-upload-button"
              startIcon={<UploadTwoToneIcon />}
              sx={{
                alignSelf: { xs: "flex-start", md: "flex-end" },
                borderRadius: 999,
                px: 2.5,
                fontWeight: 600,
              }}
            >
              Upload avatar
              <input
                hidden
                type="file"
                accept="image/*"
                onChange={(e) => setAvatarFile(e.target.files[0])}
              />
            </Button>
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions
        sx={{
          px: { xs: 3, md: 4 },
          pb: { xs: 3, md: 3.5 },
          pt: 2,
          display: "flex",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 1.5,
          background: (theme) =>
            alpha(
              theme.palette.background.paper,
              theme.palette.mode === "light" ? 0.65 : 0.4
            ),
          borderTop: (theme) => `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
        }}
      >
        <Typography variant="caption" sx={{ letterSpacing: 1.2, textTransform: "uppercase", opacity: 0.6 }}>
          Changes apply after you save
        </Typography>
        <Stack direction="row" spacing={1.5}>
          <Button
            onClick={onClose}
            data-testid="edit-agent-cancel-button"
            startIcon={<CloseRoundedIcon />}
            sx={{ borderRadius: 999, px: 2.5 }}
          >
            Cancel
          </Button>
          <Button
            disabled={saving}
            variant="contained"
            onClick={handleSave}
            data-testid="edit-agent-save-button"
            startIcon={<SaveRoundedIcon />}
            sx={{
              borderRadius: 999,
              px: 2.75,
              fontWeight: 700,
              letterSpacing: 0.4,
              backgroundImage: (theme) =>
                theme.palette.mode === "light"
                  ? `linear-gradient(120deg, ${theme.palette.primary.main}, ${accentTone})`
                  : `linear-gradient(120deg, ${alpha(theme.palette.primary.light ?? theme.palette.primary.main, 0.95)}, ${alpha(accentTone, 0.88)})`,
              boxShadow: (theme) => theme.shadows[6],
              '&:hover': {
                backgroundImage: (theme) =>
                  theme.palette.mode === "light"
                    ? `linear-gradient(120deg, ${theme.palette.primary.dark}, ${accentTone})`
                    : `linear-gradient(120deg, ${alpha(theme.palette.primary.light ?? theme.palette.primary.main, 1)}, ${alpha(accentTone, 0.95)})`,
                boxShadow: (theme) => theme.shadows[9],
              },
            }}
          >
            {saving ? "Saving…" : "Save"}
          </Button>
        </Stack>
      </DialogActions>
    </Dialog>
  );
}
